import { createTheme, alpha } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#113826',
      light: '#376C15',
      dark: '#1A3607',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ff851b',
      light: '#fb8117',
      dark: '#e47819',
      contrastText: '#ffffff',
    },
    accent: {
      main: '#e4ac3a',
      light: '#FD8445',
      dark: '#E55913',
    },
    background: {
      default: '#FFFFFF',
      paper: '#F4F4F4',
    },
    light: {
      default: "#fefefe",
      main: "#fefefe",
    },
    chart: {
      primary: '#264F0B',
      secondary: '#FC6C22',
      success: '#4CAF50',
      warning: '#FFA726',
      error: '#EF5350',
      purple: '#9C27B0',
      info: '#29B6F6',
    },
    status: {
      active: '#4CAF50',
      pending: '#FFA726',
      expired: '#EF5350',
      inactive: '#9E9E9E',
    },
    // Enhanced text colors for WCAG AA compliance
    text: {
      primary: 'rgba(0, 0, 0, 0.87)', // High contrast (WCAG AAA compliant)
      secondary: 'rgba(0, 0, 0, 0.70)', // Improved from 0.6 for better readability
      disabled: 'rgba(0, 0, 0, 0.50)', // Improved from 0.38 for visibility
    },
    action: {
      disabled: 'rgba(0, 0, 0, 0.38)',
      disabledBackground: 'rgba(0, 0, 0, 0.12)',
      hover: 'rgba(0, 0, 0, 0.04)',
      selected: 'rgba(0, 0, 0, 0.08)',
      focus: 'rgba(0, 0, 0, 0.12)',
    },
  },
  breakpoints: {
    values: {
      xs: 0,      // Mobile phones (portrait)
      sm: 600,    // Large phones / Small tablets (portrait)
      md: 768,    // Tablets (portrait) / Large phones (landscape)
      lg: 1024,   // Tablets (landscape) / Small laptops
      xl: 1280,   // Desktop
      xxl: 1920,  // Large desktop
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    // Fluid typography for better mobile scaling
    h1: {
      fontSize: 'clamp(2rem, 5vw, 3.5rem)',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: 'clamp(1.75rem, 4vw, 3rem)',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: 'clamp(1.5rem, 3.5vw, 2.5rem)',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h4: {
      fontSize: 'clamp(1.25rem, 3vw, 2rem)',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: 'clamp(1.125rem, 2.5vw, 1.5rem)',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: 'clamp(1rem, 2vw, 1.25rem)',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: 'clamp(1rem, 2.5vw, 1.0625rem)', // 16px → 17px (mobile-first readability)
      lineHeight: 1.6,
      letterSpacing: '0.00938em', // Slightly better letter spacing
    },
    body2: {
      fontSize: 'clamp(0.875rem, 2vw, 0.9375rem)', // 14px → 15px
      lineHeight: 1.6, // Improved from 1.5 for better readability
      letterSpacing: '0.01071em',
    },
    button: {
      fontSize: 'clamp(0.875rem, 2vw, 1rem)',
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.02857em', // Better button text spacing
    },
  },
  spacing: 8, // Base spacing unit
  // Enhanced spacing system for better mobile layouts
  customSpacing: {
    section: {
      xs: 6,  // 48px
      sm: 8,  // 64px  
      md: 10, // 80px
      lg: 12, // 96px
      xl: 16, // 128px
    },
    component: {
      xs: 2,  // 16px
      sm: 3,  // 24px
      md: 4,  // 32px
      lg: 6,  // 48px
    },
    container: {
      xs: 2,  // 16px padding
      sm: 3,  // 24px padding
      md: 4,  // 32px padding
      lg: 6,  // 48px padding
    },
  },
  components: {
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: 'none',
          backgroundColor: '#ffffff',
          '& .MuiDataGrid-main': {
            borderRadius: 12,
            overflow: 'hidden',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
          },
          '& .MuiDataGrid-row': {
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.02)',
            },
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '1px solid rgba(0, 0, 0, 0.1)',
          },
          // Enhanced responsive styling
          '& .MuiDataGrid-overlay': {
            background: 'rgba(255, 255, 255, 0.8)',
          },
          '& .MuiDataGrid-virtualScroller': {
            // Ensures scrollbars work properly
            overflow: 'auto',
          },
          '& .MuiDataGrid-virtualScrollerContent': {
            // Ensures content fills available space
            minHeight: '100%',
          },
          '& .MuiDataGrid-virtualScrollerRenderZone': {
            width: '100%',
          },
          // Better scrollbar styling
          '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
          },
          // Responsive adjustments
          [`@media (max-width: ${960}px)`]: {
            '& .MuiDataGrid-toolbarContainer': {
              flexDirection: 'column',
              gap: '8px',
            },
            '& .MuiDataGrid-cell': {
              padding: '12px',
            },
          },
        },
        columnHeader: {
          fontWeight: 600,
          color: 'rgba(0, 0, 0, 0.7)',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          // Ensures horizontal scrolling works well
          overflowX: 'auto',
          '&::-webkit-scrollbar': {
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          border: '1px solid',
          borderColor: 'rgba(0, 0, 0, 0.08)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
            borderColor: 'rgba(17, 56, 38, 0.1)',
          },
          '&.dashboard-stat-card': {
            background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid',
            borderColor: 'rgba(0, 0, 0, 0.05)',
          },
          '&.dashboard-chart-card': {
            padding: 20,
            height: '100%',
          },
          '&.api-key-card': {
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 20px 0 rgba(0,0,0,0.12)'
            }
          },
          '&.interactive-card': {
            cursor: 'pointer',
            userSelect: 'none',
            WebkitTapHighlightColor: 'transparent',
            '&:active': {
              transform: 'scale(0.98)',
              transition: 'transform 0.1s ease',
            },
            // Mobile-specific touch feedback
            [`@media (max-width: 768px)`]: {
              '&:hover': {
                transform: 'none', // Disable hover on mobile
              },
              '&:active': {
                transform: 'scale(0.96)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              },
            },
          },
          // Better mobile padding
          [`@media (max-width: 768px)`]: {
            borderRadius: 12,
            margin: '0 4px',
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 24,
          '&:last-child': {
            paddingBottom: 24,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          // Ensure proper touch targets for clickable chips
          minHeight: 36, // Comfortable touch target
          '&.MuiChip-clickable': {
            minHeight: 44, // Meet 44px minimum for interactive elements
            padding: '8px 12px',
          },
          '&.MuiChip-outlined': {
            borderWidth: 1.5,
          },
          '&.status-chip': {
            fontWeight: 600,
            padding: '0 12px',
          },
          '&.api-status-chip': {
            fontWeight: 500
          },
          // Mobile optimization
          [`@media (max-width: 600px)`]: {
            '&.MuiChip-clickable': {
              minHeight: 48, // Larger touch target on mobile
              padding: '10px 14px',
              fontSize: '0.9375rem',
            },
          },
        },
        icon: {
          fontSize: '1rem',
          marginLeft: 8,
          marginRight: -4,
        },
        deleteIcon: {
          fontSize: '1.25rem',
          marginRight: 6,
          marginLeft: -4,
          // Ensure delete icon is large enough to tap
          '&:hover': {
            color: 'error.main',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: 'none',
          minHeight: 44, // Better touch target for mobile
          padding: '12px 24px',
          fontSize: 'clamp(0.875rem, 2vw, 1rem)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: 'none',
            transform: 'translateY(-2px)',
          },
          '&:active': {
            transform: 'translateY(0) scale(0.98)',
          },
          '&.dashboard-action': {
            minWidth: 160,
            padding: '10px 20px',
            background: 'linear-gradient(45deg, #264F0B 30%, #376C15 90%)',
            '&:hover': {
              background: 'linear-gradient(45deg, #1A3607 30%, #264F0B 90%)',
            },
          },
          // Mobile-specific styles
          [`@media (max-width: 768px)`]: {
            minHeight: 48,
            padding: '14px 20px',
            fontSize: '1rem',
          },
        },
        contained: {
          padding: '12px 24px',
          boxShadow: '0 4px 12px rgba(17, 56, 38, 0.2)',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(17, 56, 38, 0.3)',
            transform: 'translateY(-2px)',
          },
        },
        outlined: {
          padding: '12px 24px',
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
            transform: 'translateY(-1px)',
          },
        },
        large: {
          padding: '16px 32px',
          fontSize: '1.125rem',
          minHeight: 56,
        },
      },
      variants: [
        {
          props: { variant: 'worldexplore' },
          style: {
            background: 'linear-gradient(45deg, #113826 30%, #376C15 90%)',
            color: 'white',
            borderRadius: 12,
            textTransform: 'none',
            fontWeight: 600,
            padding: '14px 28px',
            minHeight: 48,
            boxShadow: '0 4px 14px rgba(17, 56, 38, 0.25)',
            '&:hover': {
              background: 'linear-gradient(45deg, #0d2a1c 30%, #2a5411 90%)',
              transform: 'translateY(-3px)',
              boxShadow: '0 8px 25px rgba(17, 56, 38, 0.35)',
            },
            '&:active': {
              transform: 'translateY(-1px) scale(0.98)',
            },
          },
        },
        {
          props: { variant: 'cta' },
          style: {
            background: 'linear-gradient(135deg, #ff851b 0%, #e47819 100%)',
            color: 'white',
            borderRadius: 12,
            fontWeight: 700,
            padding: '16px 32px',
            fontSize: '1.125rem',
            boxShadow: '0 6px 20px rgba(255, 133, 27, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #e47819 0%, #d06d15 100%)',
              transform: 'translateY(-3px)',
              boxShadow: '0 10px 30px rgba(255, 133, 27, 0.4)',
            },
          },
        },
      ],
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: 'clamp(16px, 4vw, 24px)',
          paddingRight: 'clamp(16px, 4vw, 24px)',
          '@media (max-width: 768px)': {
            paddingLeft: '16px',
            paddingRight: '16px',
            maxWidth: '100%',
          },
        },
        maxWidthLg: {
          '@media (min-width: 1024px)': {
            maxWidth: '1200px',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            minHeight: 56, // Better touch target
            fontSize: '16px', // Prevents zoom on iOS
            transition: 'all 0.3s ease',
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(17, 56, 38, 0.5)',
              },
            },
            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#113826',
                borderWidth: '2px',
              },
            },
            [`@media (max-width: 768px)`]: {
              '& input': {
                padding: '16px 14px',
                fontSize: '16px', // Critical for preventing zoom on iOS
              },
            },
          },
          '& .MuiInputLabel-root': {
            fontSize: '1rem',
            '&.Mui-focused': {
              color: '#113826',
            },
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: 8,
        },
        track: {
          borderRadius: 22 / 2,
          opacity: 0.2,
        },
        thumb: {
          borderRadius: '50%',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          height: 8,
          backgroundColor: alpha('#264F0B', 0.1),
        },
        bar: {
          borderRadius: 8,
        },
      },
    },
    // New additions for dashboard
    MuiIconButton: {
      defaultProps: {
        // Ensure touch target accessibility
        disableRipple: false,
      },
      styleOverrides: {
        root: {
          // Base touch target - 48x48px default (exceeds 44x44px minimum)
          minWidth: 48,
          minHeight: 48,
          padding: 12,
          '&.stat-icon': {
            width: 48,
            height: 48,
            borderRadius: 12,
            backgroundColor: alpha('#264F0B', 0.1),
            color: '#264F0B',
            '&:hover': {
              backgroundColor: alpha('#264F0B', 0.2),
            },
          },
        },
        // Small size - ensure 44x44px minimum
        sizeSmall: {
          minWidth: 44,
          minHeight: 44,
          padding: 10,
          fontSize: '1.25rem',
        },
        // Medium size - 48x48px (default)
        sizeMedium: {
          minWidth: 48,
          minHeight: 48,
          padding: 12,
          fontSize: '1.5rem',
        },
        // Large size - 56x56px
        sizeLarge: {
          minWidth: 56,
          minHeight: 56,
          padding: 16,
          fontSize: '1.75rem',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.dashboard-list-item': {
            borderRadius: 8,
            marginBottom: 8,
            '&:hover': {
              backgroundColor: alpha('#264F0B', 0.05),
            },
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: alpha('#000000', 0.9),
          padding: '8px 12px',
          fontSize: '0.75rem',
          borderRadius: 6,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          // Ensure proper touch targets for toggle buttons
          minHeight: 44,
          minWidth: 44,
          padding: '10px 16px',
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: alpha('#264F0B', 0.08),
          },
          '&.Mui-selected': {
            backgroundColor: alpha('#264F0B', 0.12),
            color: '#264F0B',
            '&:hover': {
              backgroundColor: alpha('#264F0B', 0.16),
            },
          },
          // Mobile optimization
          [`@media (max-width: 600px)`]: {
            minHeight: 48,
            minWidth: 48,
            padding: '12px 18px',
            fontSize: '0.9375rem',
          },
        },
        sizeSmall: {
          minHeight: 44,
          minWidth: 44,
          padding: '8px 12px',
          fontSize: '0.875rem',
        },
        sizeMedium: {
          minHeight: 48,
          minWidth: 48,
          padding: '10px 16px',
        },
        sizeLarge: {
          minHeight: 56,
          minWidth: 56,
          padding: '14px 24px',
          fontSize: '1.125rem',
        },
      },
    },
    MuiBenefitCard: {
      styleOverrides: {
        root: {
          height: {
            xs: '300px',
            sm: '400px',
            md: '500px',
            lg: '600px'
          },
          width: {
            xs: '280px',
            sm: '350px',
            md: '450px',
            lg: '500px'
          }
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.sidebar-item': {
            borderRadius: 8,
            margin: '2px 8px',
            padding: '8px 12px',
            transition: 'all 0.2s ease-in-out',
            '& .MuiListItemIcon-root': {
              minWidth: 40,
              color: 'text.secondary',
            },
            '& .MuiListItemText-primary': {
              fontSize: '1.25rem',
              fontWeight: 1500,
            },
            '&:hover': {
              backgroundColor: alpha('#113826', 0.08),
            },
            '&.active': {
              backgroundColor: alpha('#113826', 0.12),
              color: 'primary.main',
              '& .MuiListItemIcon-root': {
                color: 'primary.main',
              },
            },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          '&.sidebar-icon': {
            minWidth: 40,
            '& svg': {
              fontSize: '1.25rem',
            },
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: 'background.paper', // Use the same color as sidebar
          borderBottom: '1px solid',
          borderColor: 'divider',
        }
      }
    },
    MuiLayout: {
      defaultProps: {
        headerHeight: {
          xs: 56,  // Mobile header height
          sm: 64,  // Tablet header height
          md: 72   // Desktop header height
        },
        sidebarWidth: 280,
        zIndex: {
          statusBar: 1250,        // PWA status bar overlay
          header: 1200,           // Header/AppBar
          sidebar: 1100,          // Sidebar navigation
          drawer: 1150,           // Temporary drawers
          modal: 1300,            // Modals and dialogs
          installPrompt: 9998,    // Install prompt (mini)
          installPromptExpanded: 9999, // Install prompt (expanded)
          cookieConsent: 1400,    // Cookie consent
          snackbar: 1350,         // Snackbars and notifications
          tooltip: 1500           // Tooltips (highest)
        },
        // Add PWA-specific spacing
        pwa: {
          sidebarTopSpacing: {
            standalone: 'max(env(safe-area-inset-top), 20px)',
            browser: '0px'
          }
        }
      }
    }
  },
  // Add custom components for data tables
  customComponents: {
    dataTable: {
      wrapper: {
        width: '100%',
        overflow: 'hidden',
        borderRadius: 12,
        boxShadow: '0 2px 12px 0 rgba(0,0,0,0.05)',
      },
      card: {
        p: 0,
        overflow: 'hidden',
        height: '100%',
      },
      toolbar: {
        p: 2,
        backgroundColor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
      },
      filterContainer: {
        display: 'flex',
        flexDirection: {
          xs: 'column',
          sm: 'row'
        },
        gap: 2,
        p: 2,
        backgroundColor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
      },
      gridContainer: {
        height: {
          xs: '400px',
          sm: '500px',
          md: '600px',
        },
        width: '100%',
      },
    }
  },
  transitions: {
    easing: {
      sharp: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    },
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
  },
  // Custom values for dashboard
  customValues: {
    cardGradients: {
      primary: 'linear-gradient(135deg, #e4ac3a 0%, #b28f5f 100%)',
      secondary: 'linear-gradient(135deg, #ff851b 0%, #ff9d45 100%)',
      accent: 'linear-gradient(135deg, #e4ac3a 0%, #b28f5f 100%)',
      success: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
      warning: 'linear-gradient(135deg, #FFA726 0%, #FB8C00 100%)',
      error: 'linear-gradient(135deg, #EF5350 0%, #E53935 100%)',
      enterprise: 'linear-gradient(135deg, #6B46C1 0%, #553C9A 100%)',
      professional: 'linear-gradient(135deg, #2B6CB0 0%, #2C5282 100%)',
      basic: 'linear-gradient(135deg, #38A169 0%, #276749 100%)',
      free: 'linear-gradient(135deg, #718096 0%, #4A5568 100%)'
    },
    shadows: {
      card: '0 4px 20px 0 rgba(156, 121, 69, 0.05)',
      button: '0 2px 8px 0 rgba(156, 121, 69, 0.1)',
      tooltip: '0 2px 12px 0 rgba(156, 121, 69, 0.1)',
    },
    borderRadius: {
      xs: 4,
      sm: 8,
      md: 12,
      lg: 16,
      xl: 24,
    },
    sections: {
      benefits: {
        carousel: {
          height: {
            xs: '350px',
            sm: '450px',
            md: '550px',
            lg: '650px'
          }
        }
      }
    }
  }
});

export default theme;